import React, { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { Button } from "@/components/ui/button";
import { Upload, Trash2, Eye, Edit2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ExerciseType, reverseExerciseTypeMap } from './types';
import { EditorProps } from './EditorProps';
import * as yup from "yup";

import { uploadVideo } from "@/services/shared/media/media.service";
import { VideoPlayer } from "@/components/shared/VideoPlayer";

interface VideoPreviewProps {
  videoUrl: string;
  title: string;
  readOnly: boolean;
  onTogglePreview: () => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  videoUrl,
  title,
  readOnly,
  onTogglePreview,
}) => {
  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onTogglePreview}
            className="flex items-center gap-2"
          >
            <Edit2 className="h-4 w-4" />
            Edit Mode
          </Button>
        </div>
      )}
      
      <Card className="p-4">
        {videoUrl && (
          <div className="mb-4">
            <VideoPlayer src={videoUrl} />
          </div>
        )}
        
        <h3 className="font-medium text-lg mb-2">{title}</h3>
      </Card>
    </div>
  );
};

interface VideoUploaderProps {
  videoUrl: string;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  onButtonClick: () => void;
  onVideoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({
  videoUrl,
  isUploading,
  uploadProgress,
  error,
  onButtonClick,
  onVideoUpload,
}) => {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">Upload de Vídeo</label>
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant={videoUrl ? "destructive" : "outline"}
          onClick={onButtonClick}
          disabled={isUploading}
        >
          {videoUrl ? (
            <Trash2 className="w-4 h-4 mr-2" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {videoUrl
            ? "Remover Vídeo"
            : isUploading
            ? "Enviando..."
            : "Enviar Vídeo"}
        </Button>
        {!videoUrl && (
          <input
            id="video-upload"
            type="file"
            accept="video/*"
            className="hidden"
            onChange={onVideoUpload}
            disabled={isUploading}
          />
        )}
      </div>
      {isUploading && (
        <div className="mt-2">
          <Progress value={uploadProgress} className="w-full" />
          <p className="text-sm text-gray-500 mt-1">
            {uploadProgress}% enviado
          </p>
        </div>
      )}
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
    </div>
  );
};

interface VideoFormProps {
  title: string;
  onTitleChange: (value: string) => void;
}

const VideoForm: React.FC<VideoFormProps> = ({
  title,
  onTitleChange,
}) => {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">Título *</label>
      <Input
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Digite o título do vídeo"
      />
    </div>
  );
};

/**
 * Editor de exercícios de vídeo usando a nova arquitetura
 */
export const VideoEditor: React.FC<EditorProps> = ({
  value,
  onChange,
  onValidate,
  readOnly = false
}) => {
  // Extrair dados relevantes do exercício, tratando dados JSON
  const getExerciseData = () => {
    if (!value.data) return {};
    
    // Se os dados são uma string, fazer parse
    if (typeof value.data === 'string') {
      try {
        return JSON.parse(value.data);
      } catch (error) {
        console.error("Erro ao fazer parse dos dados do exercício:", error);
        return {};
      }
    }
    
    // Se já é um objeto, usar diretamente
    return value.data;
  };
  
  const exerciseData = getExerciseData();
  const [videoUrl, setVideoUrl] = useState(exerciseData.videoUrl || "");
  const [video, setVideo] = useState(exerciseData.video || "");
  const [title, setTitle] = useState(exerciseData.title || "");
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  // Atualizar campos quando os dados do exercício mudarem
  useEffect(() => {
    const currentExerciseData = getExerciseData();
    setVideoUrl(currentExerciseData.videoUrl || "");
    setVideo(currentExerciseData.video || "");
    setTitle(currentExerciseData.title || "");
  }, [value.data]);

  // Validação usando Yup
  const videoSchema = useMemo(
    () =>
      yup.object({
        videoUrl: yup.string().required("Vídeo obrigatório"),
        title: yup.string().required("Título obrigatório"),
      }),
    []
  );

  useEffect(() => {
    videoSchema
      .validate({ videoUrl, title }, { abortEarly: true })
      .then(() => {
        if (onValidate) onValidate(true);
        setError((prev) => (prev && prev.includes("obrigatório") ? null : prev));
      })
      .catch((err) => {
        if (onValidate) onValidate(false);
        if (err instanceof yup.ValidationError) {
          setError(err.message);
        }
      });
  }, [videoUrl, title, onValidate, videoSchema]);

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      try {
        const uploadResults = await uploadVideo(file, (progress) => {
          setUploadProgress(progress);
        });

        // Store the filename as the canonical videoUrl. getVideoUrl resolves it
        // to the full API URL at render time, which keeps the preview stable
        // (no full-URL → filename flip that would remount Plyr mid-upload).
        setVideoUrl(uploadResults.video);
        setVideo(uploadResults.video);
        emitChange(uploadResults.video, uploadResults.video, title);
      } catch (error) {
        setError("Falha ao fazer upload do vídeo. Tente novamente.");
        console.error("Erro ao fazer upload do vídeo:", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveVideo = () => {
    setVideoUrl("");
    setVideo("");
    setError(null);
    setUploadProgress(0);
    // Reset file input value to allow re-uploading the same file if needed
    const fileInput = document.getElementById("video-upload") as HTMLInputElement;
    if (fileInput) {
        fileInput.value = "";
    }
    emitChange("", "", title);
  };

  const handleButtonClick = () => {
    if (videoUrl) {
      handleRemoveVideo();
    } else {
      document.getElementById("video-upload")?.click();
    }
  };

  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // Emit changes to parent
  const emitChange = (
    videoUrl: string,
    video: string,
    title: string
  ) => {
    const newValue = {
      ...value,
      type: reverseExerciseTypeMap[value.type] || reverseExerciseTypeMap[ExerciseType.Video],
      data: {
        ...value.data,
        videoUrl,
        video,
        title
      }
    };
    
    onChange(newValue);
  };

  // Modo de visualização (preview)
  if (isPreview || readOnly) {
    return (
      <VideoPreview
        videoUrl={videoUrl}
        title={title}
        readOnly={readOnly}
        onTogglePreview={togglePreview}
      />
    );
  }

  // Modo de edição
  return (
    <Card className="p-6 space-y-6">
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={togglePreview}
          className="flex items-center gap-2"
          disabled={!videoUrl || !title}
        >
          <Eye className="h-4 w-4" />
          Preview Mode
        </Button>
      </div>
      
      <div className="space-y-4">
        <VideoUploader
          videoUrl={videoUrl}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          error={error}
          onButtonClick={handleButtonClick}
          onVideoUpload={handleVideoUpload}
        />

        {videoUrl && (
          <div>
            <label className="block text-sm font-medium mb-1">Prévia</label>
            <VideoPlayer src={videoUrl} />
          </div>
        )}

        <VideoForm
          title={title}
          onTitleChange={(newTitle) => {
            setTitle(newTitle);
            emitChange(videoUrl, video, newTitle);
          }}
        />
      </div>
    </Card>
  );
};